import { useRef, useState } from 'react';
import { X, Flame } from 'lucide-react';
import { fetchCustomerDetail, type TopCustomerRow, type CustomerDetail } from '../../../services/analytics-service';

const money = (cents: number) => `$${(cents / 100).toFixed(2)}`;

/**
 * A customer's avatar, or their initial when they have never uploaded one.
 * Never an <img> with a null src — that renders as a broken-image icon, which
 * reads as "we lost their photo" rather than "they don't have one".
 */
function Avatar({ url, name, size }: { url: string | null; name: string; size: number }) {
  const cls = 'flex-none rounded-full object-cover';
  if (url) {
    return <img src={url} alt={name} className={cls} style={{ width: size, height: size }} />;
  }
  return (
    <span
      className="flex-none rounded-full bg-tab-inactive flex items-center justify-center font-grotesk font-bold text-body-grey"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.4) }}
    >
      {(name || '?').charAt(0).toUpperCase()}
    </span>
  );
}

export default function TopCustomers({ customers, shopId }: { customers: TopCustomerRow[]; shopId: string }) {
  const [open, setOpen] = useState<TopCustomerRow | null>(null);
  const [detail, setDetail] = useState<CustomerDetail | null>(null);
  const [error, setError] = useState(false);
  // Guards against out-of-order async resolutions: open A, quickly open B —
  // A's slow response must not overwrite B's card.
  const requestKeyRef = useRef<string | null>(null);

  async function openCard(c: TopCustomerRow) {
    setOpen(c); setDetail(null); setError(false);
    requestKeyRef.current = c.customer_key;
    try {
      const d = await fetchCustomerDetail(shopId, c.customer_key);
      if (requestKeyRef.current === c.customer_key) setDetail(d);
    } catch {
      if (requestKeyRef.current === c.customer_key) setError(true);
    }
  }

  function closeCard() {
    requestKeyRef.current = null;
    setOpen(null);
  }

  return (
    <div className="bg-white rounded-[10px] p-[18px]">
      <p className="font-heading text-[9px] text-body-grey mb-3.5">TOP CUSTOMERS</p>
      {customers.length === 0 ? (
        <p className="font-body text-sm text-body-grey">NO CUSTOMERS YET — SHARE YOUR QR</p>
      ) : (
        <div className="flex flex-col gap-2">
          {customers.map((c, i) => (
            <button key={c.customer_key} onClick={() => openCard(c)}
              className="w-full flex items-center justify-between gap-3 p-3 rounded-[10px] bg-background text-left">
              <span className="flex items-center gap-3 min-w-0">
                <span className="font-heading text-[9px] text-body-grey flex-none">#{i + 1}</span>
                <Avatar url={c.avatar_url} name={c.first_name} size={28} />
                <span className="font-body text-sm text-primary truncate">{c.first_name}</span>
                {c.streak_weeks >= 2 && (
                  <span className="flex-none flex items-center gap-1 rounded-full bg-accent-alt/20 px-1.5 py-1 font-heading text-[7px] text-gold-ink">
                    <Flame size={9} /> {c.streak_weeks}-WEEK STREAK
                  </span>
                )}
              </span>
              <span className="flex-none font-heading text-[9px] text-pixel-cyan">{c.stamps} STAMPS</span>
            </button>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center safe-modal bg-black/40 backdrop-blur-sm" onClick={closeCard}>
          <div className="w-full max-w-md rounded-[16px] bg-white p-5 sm:p-8 relative max-h-full overflow-y-auto" onClick={e => e.stopPropagation()}>
            <button onClick={closeCard} aria-label="Close" className="absolute top-1 right-1 p-3 text-body-grey hover:text-primary"><X size={20} /></button>
            <div className="flex items-center gap-3 mb-6">
              <Avatar url={open.avatar_url} name={open.first_name} size={48} />
              <h3 className="font-grotesk font-bold text-xl text-primary">{open.first_name}</h3>
            </div>
            {error && <p className="font-heading text-[9px] text-error">COULD NOT LOAD — TRY AGAIN</p>}
            {!detail && !error && <p className="font-heading text-[9px] text-pixel-cyan animate-pulse">LOADING…</p>}
            {detail && (
              <div className="space-y-3 font-body text-sm text-body-grey">
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-background rounded-[10px] p-3 text-center">
                    <p className="font-grotesk font-bold text-xl text-primary">{detail.total_stamps}</p>
                    <p className="font-heading text-[7px] text-body-grey mt-1">STAMPS</p>
                  </div>
                  <div className="bg-background rounded-[10px] p-3 text-center">
                    <p className="font-grotesk font-bold text-xl text-primary">{money(detail.est_spend_cents)}</p>
                    <p className="font-heading text-[7px] text-body-grey mt-1">EST. SPEND (STAMPS × YOUR AVG PRICE)</p>
                  </div>
                </div>
                <p className="font-heading text-[10px] text-body-grey">FREE COFFEES: {detail.free_coffees}</p>
                <p className="font-heading text-[10px] text-body-grey">COMPS: {detail.comps_used} USED / {detail.comps_received} SENT</p>
                {detail.badges?.length > 0 && (
                  <p className="font-heading text-[10px] text-gold-ink">BADGES: {detail.badges.join(' · ')}</p>
                )}
                {detail.streak_weeks >= 2 && (
                  <p className="font-heading text-[10px] text-gold-ink flex items-center gap-1">
                    <Flame size={12} /> {detail.streak_weeks}-WEEK STREAK
                  </p>
                )}
                <p className="font-heading text-[8px] text-body-grey">
                  FIRST VISIT {detail.first_visit ? new Date(detail.first_visit).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                  {' · '}LAST {detail.last_visit ? new Date(detail.last_visit).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }) : '—'}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
